import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { registerCliente, loginCliente } from "../../services/authCliente";
import "./LoginPage.css";

/* Las dos formas de cuenta. El detalle de cada una esta para que el que se
   registra no tenga que adivinar cual le toca: "comercial" a secas se presta
   a que un taller chico elija individual por las dudas. */
const TIPOS_CUENTA = [
  {
    valor: "individual",
    nombre: "Individual",
    detalle: "Purchasing for yourself",
  },
  {
    valor: "comercial",
    nombre: "Company",
    detalle: "Purchasing for a business",
  },
];

/* El codigo de pais se limpia mientras se escribe: el "+" adelante y hasta
   seis digitos, nada mas. Se escribe a mano y no se elige de una lista, asi
   que sin esto llegarian cosas como "+1 (US)" o "00 502" a una columna que
   el backend valida como +NNNN. El "+" se pone solo: nadie tiene por que
   acordarse de escribirlo. */
const soloCodigoPais = (valor) => {
  const texto = String(valor ?? "");
  if (texto.trim() === "") return "";

  const digitos = texto
    .replace(/\D/g, "")
    // Ningun codigo de pais empieza con cero: el que escribe "00502" esta
    // poniendo el prefijo internacional de su linea, que no es parte del
    // codigo y lo dejaria guardado como +00502.
    .replace(/^0+/, "")
    .slice(0, 6);

  return `+${digitos}`;
};

/**
 * Acceso y alta de clientes del store.
 *
 * Es la misma pantalla en dos modos porque el formulario se repite casi
 * entero y el que llega desde el checkout puede equivocarse de puerta:
 * pasar de "crear cuenta" a "entrar" tiene que ser un click, no volver
 * atras y navegar de nuevo.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState(() => {
    return location.state?.mode === "login" ? "login" : "register";
  });

  const [loading, setLoading] = useState(false);
  const [uiMessage, setUiMessage] = useState({ type: "", text: "" });
  const [form, setForm] = useState({
    tipo_cliente: "individual",
    razon_social: "",
    codigo_telefono: "",
    telefono: "",
    nombre: "",
    apellido: "",
    correo: "",
    usuario: "",
    password: "",
    password2: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("cliente_token");
    let cliente = null;
    try {
      cliente = JSON.parse(localStorage.getItem("cliente"));
    } catch {}

    if (token && cliente) {
      const redirectTo = location.state?.redirectTo || "/store/account";
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, location.state]);

  const esRegistro = mode === "register";
  const esComercial = form.tipo_cliente === "comercial";

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: name === "codigo_telefono" ? soloCodigoPais(value) : value,
    });
  };

  /* El aviso se limpia al cambiar de modo: un "user or password is wrong"
     no tiene nada que ver con el formulario de alta que queda a la vista. */
  const cambiarModo = (nuevo) => {
    if (loading) return;
    setUiMessage({ type: "", text: "" });
    setMode(nuevo);
  };

  /* La confirmacion no viaja al servidor: es un control contra el tipeo, no
     un dato de la cuenta. Se compara aca y no se manda. */
  const passwordNoCoincide =
    esRegistro && form.password2 !== "" && form.password !== form.password2;

  const handleSubmit = async (e) => {
    e.preventDefault();

    /* El aviso de que no coinciden ya esta debajo del campo; repetirlo
       arriba seria decir dos veces lo mismo. Alcanza con llevar el cursor
       ahi, que es donde hay que corregir. */
    if (esRegistro && form.password !== form.password2) {
      document.getElementById("acc-password2")?.focus();
      return;
    }

    setLoading(true);

    setUiMessage({ type: "", text: "" });
    try {
      let response;

      if (mode === "register") {
        response = await registerCliente(form);
      } else {
        response = await loginCliente(form.usuario, form.password);
      }

      localStorage.setItem("cliente_token", response.token);
      localStorage.setItem("cliente", JSON.stringify(response.cliente));

      const redirectTo = location.state?.redirectTo || "/store/account";
      navigate(redirectTo);
    } catch (error) {
      const msg =
        error.response?.data?.message || "We couldn't connect to the server.";
      setUiMessage({ type: "error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStore = () => {
    navigate("/store");
  };

  return (
    <div className="acc-pagina">
      <div className={`acc-caja${esRegistro ? " acc-caja--ancha" : ""}`}>
        {/* La version clara del logo, la misma que usa el pie del store. */}
        <img
          className="acc-logo"
          src="/image/ADC-Logo2.png"
          alt="ADC Aircraft Service &amp; Part"
        />

        <h1 className="acc-titulo">
          {esRegistro ? "Create account" : "Sign in"}
        </h1>

        <p className="acc-sub">
          {esRegistro
            ? "An account lets you place the order and follow it."
            : "Place orders and follow them from your account."}
        </p>

        {/* role="alert" para que el lector de pantalla lo anuncie al
            aparecer: el foco queda en el boton y el aviso sale arriba. */}
        {uiMessage.text ? (
          <div className="acc-aviso" role="alert">
            {uiMessage.text}
          </div>
        ) : null}

        <form
          className={`acc-form${esRegistro ? " acc-form--ancho" : ""}`}
          onSubmit={handleSubmit}
        >
          {/* El alta va en dos columnas: en una sola columna son nueve
              renglones y la tarjeta se vuelve una tira que no entra en
              pantalla. Los campos se emparejan por peso, no por orden
              alfabetico: los dos del nombre juntos, los dos de la
              contrasena juntos. */}
          <div className={`acc-rejilla${esRegistro ? " acc-rejilla--dos" : ""}`}>
            {esRegistro ? (
              <>
                {/* Ocupa las dos columnas y va primero porque decide el
                    resto del formulario: elegir "Company" agrega la razon
                    social y el telefono. */}
                <fieldset className="acc-tipos acc-total">
                  <legend className="acc-etiqueta">Account type</legend>

                  <div className="acc-tipos-grid">
                    {TIPOS_CUENTA.map((t) => (
                      <label className="acc-tipo" key={t.valor}>
                        {/* Radio de verdad y no un div con onClick: asi
                            funcionan las flechas del teclado, el foco y el
                            lector de pantalla sin tener que rehacerlos. */}
                        <input
                          type="radio"
                          name="tipo_cliente"
                          value={t.valor}
                          checked={form.tipo_cliente === t.valor}
                          onChange={handleChange}
                          disabled={loading}
                        />
                        <span className="acc-tipo-caja">
                          <span className="acc-tipo-nombre">{t.nombre}</span>
                          <span className="acc-tipo-detalle">{t.detalle}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {esComercial ? (
                  <>
                    <div className="acc-campo">
                      <label className="acc-etiqueta" htmlFor="acc-empresa">
                        Legal company name
                      </label>
                      <input
                        className="acc-input"
                        id="acc-empresa"
                        name="razon_social"
                        autoComplete="organization"
                        maxLength={200}
                        value={form.razon_social}
                        onChange={handleChange}
                        disabled={loading}
                        required
                      />
                    </div>

                    {/* Codigo y numero separados, como los guarda la base.
                        Los dos juntos en un campo suelto despues no hay
                        forma de saber donde termina uno y empieza el otro. */}
                    <div className="acc-fila-tel">
                      <div className="acc-campo">
                        <label className="acc-etiqueta" htmlFor="acc-codigo-tel">
                          Country code
                        </label>
                        <input
                          className="acc-input"
                          id="acc-codigo-tel"
                          name="codigo_telefono"
                          inputMode="tel"
                          autoComplete="tel-country-code"
                          placeholder="+1"
                          maxLength={7}
                          pattern="\+[0-9]{1,6}"
                          title="Country code, for example +1"
                          value={form.codigo_telefono}
                          onChange={handleChange}
                          disabled={loading}
                          required
                        />
                      </div>

                      <div className="acc-campo">
                        <label className="acc-etiqueta" htmlFor="acc-telefono">
                          Phone number
                        </label>
                        <input
                          className="acc-input"
                          id="acc-telefono"
                          name="telefono"
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel-national"
                          maxLength={20}
                          value={form.telefono}
                          onChange={handleChange}
                          disabled={loading}
                          required
                        />
                      </div>
                    </div>
                  </>
                ) : null}

                <div className="acc-campo">
                  <label className="acc-etiqueta" htmlFor="acc-nombre">
                    First name
                  </label>
                  <input
                    className="acc-input"
                    id="acc-nombre"
                    name="nombre"
                    autoComplete="given-name"
                    minLength={2}
                    maxLength={100}
                    value={form.nombre}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  />
                </div>

                <div className="acc-campo">
                  <label className="acc-etiqueta" htmlFor="acc-apellido">
                    Last name
                  </label>
                  <input
                    className="acc-input"
                    id="acc-apellido"
                    name="apellido"
                    autoComplete="family-name"
                    minLength={2}
                    maxLength={100}
                    value={form.apellido}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  />
                </div>

                <div className="acc-campo">
                  <label className="acc-etiqueta" htmlFor="acc-correo">
                    {esComercial ? "Business email" : "Email"}
                  </label>
                  <input
                    className="acc-input"
                    id="acc-correo"
                    name="correo"
                    type="email"
                    autoComplete="email"
                    maxLength={120}
                    value={form.correo}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  />
                </div>
              </>
            ) : null}

            <div className="acc-campo">
              <label className="acc-etiqueta" htmlFor="acc-usuario">
                Username
              </label>
              {/* autoComplete en usuario y contrasena para que el gestor de
                  contrasenas del navegador los reconozca como un par y
                  ofrezca guardarlos. Sin esto guarda la mitad, o nada. */}
              <input
                className="acc-input"
                id="acc-usuario"
                name="usuario"
                autoComplete="username"
                minLength={esRegistro ? 3 : undefined}
                maxLength={100}
                pattern={esRegistro ? "[A-Za-z0-9._-]+" : undefined}
                title={
                  esRegistro
                    ? "Letters, numbers, dots, hyphens and underscores"
                    : undefined
                }
                value={form.usuario}
                onChange={handleChange}
                disabled={loading}
                required
              />
            </div>

            <div className="acc-campo">
              <label className="acc-etiqueta" htmlFor="acc-password">
                Password
              </label>
              <input
                className="acc-input"
                id="acc-password"
                name="password"
                type="password"
                autoComplete={esRegistro ? "new-password" : "current-password"}
                minLength={esRegistro ? 8 : undefined}
                maxLength={esRegistro ? 72 : undefined}
                value={form.password}
                onChange={handleChange}
                disabled={loading}
                required
              />
            </div>

            {esRegistro ? (
              <div className="acc-campo">
                <label className="acc-etiqueta" htmlFor="acc-password2">
                  Confirm password
                </label>
                <input
                  className="acc-input"
                  id="acc-password2"
                  name="password2"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={72}
                  value={form.password2}
                  onChange={handleChange}
                  disabled={loading}
                  required
                />
                {/* Avisa mientras escribe y no al apretar el boton:
                    enterarse ahi obliga a volver sobre los dos campos. */}
                {passwordNoCoincide ? (
                  <p className="acc-error-campo" role="alert">
                    The passwords do not match.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <button className="acc-btn" type="submit" disabled={loading}>
            {loading ? (
              <span className="acc-cargando">
                <span className="acc-spinner" />
                {esRegistro ? "Creating account" : "Signing in"}
              </span>
            ) : esRegistro ? (
              "Create account"
            ) : (
              "Sign in"
            )}
          </button>
        </form>

        <div className="acc-pie">
          <div>
            {esRegistro
              ? "Already have an account? "
              : "Don't have an account? "}
            <button
              type="button"
              className="acc-enlace"
              onClick={() => cambiarModo(esRegistro ? "login" : "register")}
              disabled={loading}
            >
              {esRegistro ? "Sign in" : "Create one"}
            </button>
          </div>

          <div>
            <button
              type="button"
              className="acc-enlace"
              onClick={handleBackToStore}
              disabled={loading}
            >
              Back to store
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
